import Fuse from "fuse.js";
import { useNavigate } from "@solidjs/router";
import { For, Show, createMemo, createSignal } from "solid-js";
import { formatId } from "../api/tickets";
import type { Ticket } from "../api/tickets";
import { tickets } from "../store/tickets";
import { paletteOpen, setPaletteOpen, setSelectedId } from "../store/ui";
import { StatusIcon } from "./StatusIcon";
import styles from "./CommandPalette.module.css";

type ResultItem =
  | { kind: "issue"; ticket: Ticket }
  | { kind: "nav"; label: string; href: string };

const NAV_ACTIONS: { kind: "nav"; label: string; href: string }[] = [
  { kind: "nav", label: "Go to Today", href: "/today" },
  { kind: "nav", label: "Go to List", href: "/list" },
  { kind: "nav", label: "Go to Tree", href: "/tree" },
];

export function CommandPalette() {
  const [query, setQuery] = createSignal("");
  const [activeIndex, setActiveIndex] = createSignal(0);
  const navigate = useNavigate();

  const fuse = createMemo(
    () => new Fuse(tickets(), { keys: ["title"], threshold: 0.35 }),
  );

  const results = createMemo<ResultItem[]>(() => {
    const q = query().trim();
    if (!q) {
      const issues: ResultItem[] = tickets()
        .slice(0, 6)
        .map((t) => ({ kind: "issue", ticket: t }));
      return [...issues, ...NAV_ACTIONS];
    }
    // ID prefix match (`B-001`, `001`, `1`) takes priority — fuzzy on title second.
    const lower = q.toLowerCase();
    const idMatches: ResultItem[] = tickets()
      .filter((t) => {
        const idStr = formatId(t.id).toLowerCase();
        return idStr.includes(lower) || String(t.id) === q;
      })
      .slice(0, 4)
      .map((t) => ({ kind: "issue", ticket: t }));
    const idMatchedIds = new Set(
      idMatches.flatMap((r) => (r.kind === "issue" ? [r.ticket.id] : [])),
    );
    const fuzzy: ResultItem[] = fuse()
      .search(q)
      .filter((r) => !idMatchedIds.has(r.item.id))
      .slice(0, 8 - idMatches.length)
      .map((r) => ({ kind: "issue", ticket: r.item }));
    const navMatches = NAV_ACTIONS.filter((a) =>
      a.label.toLowerCase().includes(lower),
    );
    return [...idMatches, ...fuzzy, ...navMatches];
  });

  const close = () => {
    setPaletteOpen(false);
    setQuery("");
    setActiveIndex(0);
  };

  const select = (item: ResultItem) => {
    if (item.kind === "issue") {
      setSelectedId(item.ticket.id);
      // Phase 4 will navigate to /issue/:id; for now selecting is enough.
    } else {
      navigate(item.href);
    }
    close();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    const list = results();
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (list.length === 0 ? 0 : (i + 1) % list.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) =>
        list.length === 0 ? 0 : (i - 1 + list.length) % list.length,
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = list[activeIndex()];
      if (item) select(item);
    }
  };

  return (
    <Show when={paletteOpen()}>
      <div class={styles.overlay} onClick={close}>
        <div class={styles.panel} onClick={(e) => e.stopPropagation()}>
          <input
            class={styles.input}
            type="text"
            placeholder="Search issues, navigate…"
            value={query()}
            onInput={(e) => {
              setQuery(e.currentTarget.value);
              setActiveIndex(0);
            }}
            onKeyDown={onKeyDown}
            autofocus
          />
          <Show
            when={results().length > 0}
            fallback={<div class={styles.empty}>No matches</div>}
          >
            <div class={styles.list}>
              <For each={results()}>
                {(item, i) => (
                  <div
                    class={styles.item}
                    classList={{ [styles.itemActive]: i() === activeIndex() }}
                    onMouseEnter={() => setActiveIndex(i())}
                    onClick={() => select(item)}
                  >
                    <Show
                      when={item.kind === "issue"}
                      fallback={
                        <>
                          <span class={styles.navIcon}>→</span>
                          <span class={styles.title}>
                            {(item as { kind: "nav"; label: string }).label}
                          </span>
                        </>
                      }
                    >
                      <StatusIcon
                        status={(item as { kind: "issue"; ticket: Ticket }).ticket.status}
                        size={11}
                      />
                      <span class={styles.id}>
                        {formatId((item as { kind: "issue"; ticket: Ticket }).ticket.id)}
                      </span>
                      <span class={styles.title}>
                        {(item as { kind: "issue"; ticket: Ticket }).ticket.title}
                      </span>
                    </Show>
                  </div>
                )}
              </For>
            </div>
          </Show>
          <div class={styles.hint}>
            <span><kbd class={styles.kbd}>↑</kbd><kbd class={styles.kbd}>↓</kbd> navigate</span>
            <span><kbd class={styles.kbd}>↵</kbd> select</span>
            <span><kbd class={styles.kbd}>esc</kbd> close</span>
          </div>
        </div>
      </div>
    </Show>
  );
}
